import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { ResultGrid } from '../../../../grid/ResultGrid';
import { Column } from '../../../../grid/Column';
import { DeleteIcon } from '../../../../icon/DeleteIcon';
import { EditIcon } from '../../../../icon/EditIcon';
import { ExternalLinkIcon } from '../../../../table/ExternalLinkIcon';
import { InfoIcon } from '../../../../icon/InfoIcon';
import {
  bulkEditEarthquakes as bulkEditRunupsAc,
  cancelEdit as cancelEditAc,
  cancelEditConfirmation as cancelEditConfirmationAc,
  openEditConfirmation as openEditConfirmationAc,
  startEdit as startEditAc,
} from '../../../../../redux/earthquakes/data/earthquakeDataActions';
import { createInitialForm, formToJson } from '../edit/earthquakeEditModel';
import {
  checkboxCell,
  latLonCell,
  textCell,
} from '../../../../grid/resultGridCells';
import {
  minColumns,
  sort as defaultSort,
  visibleColumns as defaultColumns,
} from '../../../../../redux/earthquakes/data/earthquakeReduxConstants';

const renderEditLink = id => (
  <Link aria-label={`Edit Earthquake ${id}`} to={`/view/hazards/earthquake/update/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

const renderVolcanoLink = id => (
  <Link aria-label={`Volcano ID ${id}`} to={`/view/hazards/volcano/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <ExternalLinkIcon ariaLabel="Link To Volcanic Event" tooltip="View Related Volcanic Event" />
  </Link>
);

const renderInfoLink = id => (
  <Link aria-label={`Earthquake ID ${id}`} to={`/view/hazards/earthquake/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <InfoIcon />
  </Link>
);

const renderTsunamiLink = id => (
  <Link aria-label={`Tsunami ID ${id}`} to={`/view/hazards/tsunami/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <ExternalLinkIcon ariaLabel="Link To Tsunami Event" tooltip="View Related Tsunami Event" />
  </Link>
);

const handleBeforeUnload = (event) => {
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = 'leaving earthquake';
};

class EarthquakeDataGridUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.focus = this.focus.bind(this);
    this.handleYesClick = this.handleYesClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleEditToggle = this.handleEditToggle.bind(this);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
    this.deleteCancel = null;
    this.state = {
      modal: false,
      selectedId: null,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      deleting,
      onDeleteCloseModal,
      saving,
      cancelEditConfirmation,
      errors,
      cancelEdit,
    } = this.props;
    if (!deleting && prevProps.deleting) {
      this.closeModal(() => {
        onDeleteCloseModal();
      });
    }
    if (!saving && prevProps.saving) {
      cancelEditConfirmation();
      window.removeEventListener('beforeunload', handleBeforeUnload, true);
      if (!errors.size) {
        cancelEdit();
        onDeleteCloseModal(); // reload
      }
    }
  }

  openModal(selectedId) {
    this.setState({
      modal: true,
      selectedId,
    });
  }

  closeModal(callback) {
    this.setState({
      modal: false,
      selectedId: null,
    }, callback);
  }

  handleDeleteClick(id) {
    this.openModal(id);
  }

  focus() {
    this.deleteCancel.focus();
  }

  handleYesClick() {
    const { deleteEarthquake, history } = this.props;
    const { selectedId } = this.state;
    deleteEarthquake({ id: selectedId, history });
  }

  handleSubmit(data) {
    const { bulkEditEarthquakes } = this.props;
    bulkEditEarthquakes({ data });
  }

  handleEditToggle(edit) {
    const { startEdit, cancelEdit, earthquakes } = this.props;
    if (edit) {
      const items = {};
      const initialValues = { items };
      earthquakes.forEach((item) => {
        items[`${item.get('id')}`] = createInitialForm(item).toJS();
      });
      startEdit({ initialValues });
      window.addEventListener('beforeunload', handleBeforeUnload, true);
    } else {
      cancelEdit();
      window.removeEventListener('beforeunload', handleBeforeUnload, true);
    }
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortData, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortData(sort);
      changed = true;
    }
    return changed;
  }

  render() {
    const {
      earthquakes,
      dataManager,
      sort,
      visibleColumns,
      searchParams,
      deleting,
      editConfirmationOpen,
      saving,
      formData,
      cancelEditConfirmation,
      openEditConfirmation,
      initialFormData,
      initialValues,
      editing,
      errors,
    } = this.props;

    const { modal } = this.state;

    return (
      <div className="haz-data-grid-container">

        <Modal isOpen={modal || deleting} onOpened={this.focus} backdrop="static">
          <ModalHeader>Delete Earthquake</ModalHeader>
          <ModalBody>
            {deleting ? 'Deleting...' : 'Are you sure you want to delete the earthquake?'}
          </ModalBody>
          <ModalFooter>
            <Button disabled={deleting} color="danger" onClick={this.handleYesClick}>Delete</Button>
            {' '}
            <Button disabled={deleting} color="secondary" innerRef={(ref) => { this.deleteCancel = ref; }} onClick={() => this.closeModal()}>Cancel</Button>
          </ModalFooter>
        </Modal>

        <ResultGrid
          data={earthquakes}
          sort={sort}
          columnChooser
          visibleColumns={visibleColumns}
          handleColumnsToggle={this.handleToggleColumns}
          download
          downloadPrefix="earthquakes"
          downloadSearchParams={searchParams}
          edit={dataManager}
          editing={editing}
          handleEditToggle={this.handleEditToggle}
          initialValues={initialValues}
          handleSubmit={this.handleSubmit}
          editConfirmationOpen={editConfirmationOpen}
          hazardName="Earthquakes"
          saving={saving}
          handleCancel={cancelEditConfirmation}
          formData={formData}
          initialFormData={initialFormData}
          openEditConfirmation={openEditConfirmation}
          formToJson={formToJson}
          defaultColumns={defaultColumns}
          defaultSort={defaultSort}
          minColumns={minColumns}
        >
          {dataManager && (
          <Column alwaysVisible downloadable={false} id="del" cell={({ row }) => (<DeleteIcon handleClick={() => this.handleDeleteClick(row.get('id'))} />)} width={30}>
            <DeleteIcon />
          </Column>
          )}
          {dataManager && (
          <Column alwaysVisible downloadable={false} id="edit" cell={({ row }) => renderEditLink(row.get('id'))} width={30}>
            <EditIcon />
          </Column>
          )}
          <Column alwaysVisible={dataManager} id="id" sortable width={50}>Id</Column>
          {dataManager && (
          <Column alwaysVisible id="publish" cell={({ value, row }) => checkboxCell(row, 'publish', value, errors, editing)} sortable width={45}>Publish</Column>
          )}
          <Column id="year" width={45} sortable helpModalId="year" cell={({ value, row }) => textCell(row, 'year', value, errors, editing)}>Year</Column>
          <Column id="month" width={35} sortable helpModalId="month" cell={({ value, row }) => textCell(row, 'month', value, errors, editing)}>Mo</Column>
          <Column id="day" width={35} sortable helpModalId="day" cell={({ value, row }) => textCell(row, 'day', value, errors, editing)}>Dy</Column>
          <Column id="hour" width={35} sortable helpModalId="hour" cell={({ value, row }) => textCell(row, 'hour', value, errors, editing)}>Hr</Column>
          <Column id="minute" width={35} sortable helpModalId="minute" cell={({ value, row }) => textCell(row, 'minute', value, errors, editing)}>Mn</Column>
          <Column id="second" width={35} sortable helpModalId="second" cell={({ value, row }) => textCell(row, 'second', value, errors, editing)}>Sec</Column>
          <Column id="tsunamiEventId" width={35} helpModalId="eqTsunami" cell={({ value }) => (value && renderTsunamiLink(value))}>Tsu</Column>
          <Column id="volcanoEventId" width={35} helpModalId="eqVolcano" cell={({ value }) => value && renderVolcanoLink(value)}>Vol</Column>
          <Column id="moreInfo" downloadable={false} width={40} helpModalId="eqMoreInfo" cell={({ row }) => renderInfoLink(row.get('id'))}>More Info</Column>
          <Column id="country" width={200} sortable helpModalId="eqCountry" cell={({ value, row }) => textCell(row, 'country', value, errors, editing)}>Country</Column>
          <Column id="area" width={150} sortable helpModalId="eqArea" cell={({ value, row }) => textCell(row, 'area', value, errors, editing)}>Area</Column>
          <Column id="regionCode" width={50} sortable helpModalId="region" cell={({ value, row }) => textCell(row, 'regionCode', value, errors, editing)}>Region</Column>
          <Column id="locationName" width={200} sortable helpModalId="eqLocName" cell={({ value, row }) => textCell(row, 'locationName', value, errors, editing)}>Location Name</Column>
          <Column id="latitude" width={100} sortable helpModalId="eqLatitude" cell={({ value, row }) => latLonCell(row, 'latitude', value, errors, editing)}>Latitude</Column>
          <Column id="longitude" width={100} sortable helpModalId="eqLongitude" cell={({ value, row }) => latLonCell(row, 'longitude', value, errors, editing)}>Longitude</Column>
          <Column id="eqDepth" width={80} sortable helpModalId="eqFocalDepth" cell={({ value, row }) => textCell(row, 'eqDepth', value, errors, editing)}>Focal Depth (km)</Column>
          <Column id="eqMagnitude" width={35} sortable helpModalId="eqMagnitude">Mag</Column>
          <Column id="eqMagMw" helpModalId="eqMwMag" width={35} sortable cell={({ value, row }) => textCell(row, 'eqMagMw', value, errors, editing)}>Mw</Column>
          <Column id="eqMagMs" helpModalId="eqMsMag" width={35} sortable cell={({ value, row }) => textCell(row, 'eqMagMs', value, errors, editing)}>Ms</Column>
          <Column id="eqMagMb" helpModalId="eqMbMag" width={35} sortable cell={({ value, row }) => textCell(row, 'eqMagMb', value, errors, editing)}>Mb</Column>
          <Column id="eqMagMl" helpModalId="eqMlMag" width={35} sortable cell={({ value, row }) => textCell(row, 'eqMagMl', value, errors, editing)}>Ml</Column>
          <Column id="eqMagMfa" helpModalId="eqMfaMag" width={35} sortable cell={({ value, row }) => textCell(row, 'eqMagMfa', value, errors, editing)}>Mfa</Column>
          <Column id="eqMagUnk" helpModalId="eqUnkMag" width={35} sortable cell={({ value, row }) => textCell(row, 'eqMagUnk', value, errors, editing)}>Unk</Column>
          <Column id="intensity" width={35} sortable helpModalId="eqIntensity" cell={({ value, row }) => textCell(row, 'intensity', value, errors, editing)}>MMI Int</Column>
          <Column id="deaths" width={50} sortable helpModalId="eqDeaths" cell={({ value, row }) => textCell(row, 'deaths', value, errors, editing)}>Deaths</Column>
          <Column id="deathsAmountOrder" width={80} sortable helpModalId="eqDeathsDescription" cell={({ value, row }) => textCell(row, 'deathsAmountOrder', value, errors, editing)}>Death Description</Column>
          <Column id="missing" width={50} sortable helpModalId="eqMissing" cell={({ value, row }) => textCell(row, 'missing', value, errors, editing)}>Missing</Column>
          <Column id="missingAmountOrder" width={80} sortable helpModalId="eqMissingDescription" cell={({ value, row }) => textCell(row, 'missingAmountOrder', value, errors, editing)}>Missing Description</Column>
          <Column id="injuries" width={50} sortable helpModalId="eqInjuries" cell={({ value, row }) => textCell(row, 'injuries', value, errors, editing)}>Injuries</Column>
          <Column id="injuriesAmountOrder" width={80} sortable helpModalId="eqInjuriesDescription" cell={({ value, row }) => textCell(row, 'injuriesAmountOrder', value, errors, editing)}>Injuries Description</Column>
          <Column id="damageMillionsDollars" width={60} sortable helpModalId="eqDamage" cell={({ value, row }) => textCell(row, 'damageMillionsDollars', value, errors, editing)}>Damage ($Mil)</Column>
          <Column id="damageAmountOrder" width={80} sortable helpModalId="eqDamageDescription" cell={({ value, row }) => textCell(row, 'damageAmountOrder', value, errors, editing)}>Damage Description</Column>
          <Column id="housesDestroyed" width={80} sortable helpModalId="eqHousesDestroyed" cell={({ value, row }) => textCell(row, 'housesDestroyed', value, errors, editing)}>Houses Destroyed</Column>
          <Column id="housesDestroyedAmountOrder" width={110} sortable helpModalId="eqHousesDestroyedDescription" cell={({ value, row }) => textCell(row, 'housesDestroyedAmountOrder', value, errors, editing)}>Houses Destroyed Description</Column>
          <Column id="housesDamaged" width={80} sortable helpModalId="eqHousesDamaged" cell={({ value, row }) => textCell(row, 'housesDamaged', value, errors, editing)}>Houses Damaged</Column>
          <Column id="housesDamagedAmountOrder" width={110} sortable helpModalId="eqHousesDamagedDescription" cell={({ value, row }) => textCell(row, 'housesDamagedAmountOrder', value, errors, editing)}>Houses Damaged Description</Column>
          <Column id="deathsTotal" width={50} sortable helpModalId="eqTotalDeaths" cell={({ value, row }) => textCell(row, 'deathsTotal', value, errors, editing)}>Total Deaths</Column>
          <Column id="deathsAmountOrderTotal" width={80} sortable helpModalId="eqTotalDeathsDescription" cell={({ value, row }) => textCell(row, 'deathsAmountOrderTotal', value, errors, editing)}>Total Death Description</Column>
          <Column id="missingTotal" width={50} sortable helpModalId="eqTotalMissing" cell={({ value, row }) => textCell(row, 'missingTotal', value, errors, editing)}>Total Missing</Column>
          <Column id="missingAmountOrderTotal" width={80} sortable helpModalId="eqTotalMissingDescription" cell={({ value, row }) => textCell(row, 'missingAmountOrderTotal', value, errors, editing)}>Total Missing Description</Column>
          <Column id="injuriesTotal" width={50} sortable helpModalId="eqTotalInjuries" cell={({ value, row }) => textCell(row, 'injuriesTotal', value, errors, editing)}>Total Injuries</Column>
          <Column id="injuriesAmountOrderTotal" width={80} sortable helpModalId="eqTotalInjuriesDescription" cell={({ value, row }) => textCell(row, 'injuriesAmountOrderTotal', value, errors, editing)}>Total Injuries Description</Column>
          <Column id="damageMillionsDollarsTotal" width={90} sortable helpModalId="eqTotalDamage" cell={({ value, row }) => textCell(row, 'damageMillionsDollarsTotal', value, errors, editing)}>Total Damage ($Mil)</Column>
          <Column id="damageAmountOrderTotal" width={90} sortable helpModalId="eqTotalDamageDescription" cell={({ value, row }) => textCell(row, 'damageAmountOrderTotal', value, errors, editing)}>Total Damage Description</Column>
          <Column id="housesDestroyedTotal" width={90} sortable helpModalId="eqTotalHousesDestroyed" cell={({ value, row }) => textCell(row, 'housesDestroyedTotal', value, errors, editing)}>Total Houses Destroyed</Column>
          <Column id="housesDestroyedAmountOrderTotal" width={130} sortable helpModalId="eqTotalHousesDestroyedDescription" cell={({ value, row }) => textCell(row, 'housesDestroyedAmountOrderTotal', value, errors, editing)}>Total Houses Destroyed Description</Column>
          <Column id="housesDamagedTotal" width={100} sortable helpModalId="eqTotalHousesDamaged" cell={({ value, row }) => textCell(row, 'housesDamagedTotal', value, errors, editing)}>Total Houses Damaged</Column>
          <Column id="housesDamagedAmountOrderTotal" width={125} sortable helpModalId="eqTotalHousesDamagedDescription" cell={({ value, row }) => textCell(row, 'housesDamagedAmountOrderTotal', value, errors, editing)}>Total Houses Damaged Description</Column>
        </ResultGrid>
      </div>
    );
  }
}

EarthquakeDataGridUnwrapped.propTypes = {
  earthquakes: ImmutablePropTypes.list.isRequired,
  dataManager: PropTypes.bool.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  sortData: PropTypes.func.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  searchParams: PropTypes.string.isRequired,
  deleting: PropTypes.bool.isRequired,
  deleteEarthquake: PropTypes.func.isRequired,
  history: PropTypes.shape({}).isRequired,
  onDeleteCloseModal: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  bulkEditEarthquakes: PropTypes.func.isRequired,
  editConfirmationOpen: PropTypes.bool.isRequired,
  formData: ImmutablePropTypes.map.isRequired,
  initialFormData: ImmutablePropTypes.map.isRequired,
  openEditConfirmation: PropTypes.func.isRequired,
  cancelEditConfirmation: PropTypes.func.isRequired,
  errors: ImmutablePropTypes.map.isRequired,
  startEdit: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  editing: PropTypes.bool.isRequired,
  initialValues: ImmutablePropTypes.map.isRequired,
};

const mapStateToProps = state => ({
  saving: state.getIn(['earthquakeDataPage', 'saving']),
  editConfirmationOpen: state.getIn(['earthquakeDataPage', 'editConfirmationOpen']),
  formData: state.getIn(['earthquakeDataPage', 'formData']),
  initialFormData: state.getIn(['earthquakeDataPage', 'initialFormData']),
  errors: state.getIn(['earthquakeDataPage', 'errors']),
  editing: state.getIn(['earthquakeDataPage', 'editing']),
  initialValues: state.getIn(['earthquakeDataPage', 'initialValues']),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  bulkEditEarthquakes: bulkEditRunupsAc,
  openEditConfirmation: openEditConfirmationAc,
  cancelEditConfirmation: cancelEditConfirmationAc,
  startEdit: startEditAc,
  cancelEdit: cancelEditAc,
}, dispatch);

export const EarthquakeDataGrid = connect(mapStateToProps, mapDispatchToProps)(EarthquakeDataGridUnwrapped);
